/**
 * Оформление: применение, хранение и синхронизация.
 *
 * Значение живёт в трёх местах, и у каждого своя роль:
 *   sessionStorage — мгновенное применение в этой вкладке, без сокета;
 *   Mongo          — выбор переживает закрытие браузера и переезжает на
 *                    другое устройство вместе с ключом;
 *   атрибут <html> — первый кадр уже в нужной теме, без вспышки.
 *
 * Правило разрешения конфликта: в этой вкладке выигрывает sessionStorage,
 * потому что это последнее осознанное действие человека здесь. При первом
 * открытии вкладки sessionStorage пуст, и берётся то, что пришло из Mongo
 * через SSR.
 */
import {
  DEFAULT_THEME, isTheme, THEME_SESSION_KEY, type Theme,
} from '../shared/theme.ts';
import { request } from './transport/socket.ts';

let current: Theme = DEFAULT_THEME;
const listeners = new Set<() => void>();

export function getTheme(): Theme {
  return current;
}

/** Стабильное значение для SSR: на сервере нет ни storage, ни документа. */
export function getServerTheme(): Theme {
  return DEFAULT_THEME;
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * 'auto' атрибута не ставит: тогда решает prefers-color-scheme, и системная
 * тема работает без участия JavaScript вообще.
 */
function paint(theme: Theme): void {
  if (typeof document === 'undefined') return;
  if (theme === 'auto') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = theme;
}

function readSession(): Theme | null {
  try {
    const stored = sessionStorage.getItem(THEME_SESSION_KEY);
    return isTheme(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeSession(theme: Theme): void {
  try {
    sessionStorage.setItem(THEME_SESSION_KEY, theme);
  } catch {
    /* приватный режим: тема доживёт до перезагрузки, и только */
  }
}

/** Вызывается один раз при старте, до гидратации. */
export function initTheme(fromSsr: Theme): void {
  current = readSession() ?? fromSsr;
  paint(current);
  notify();
}

/** Выбор человека: применяем сразу, сохраняем, потом сообщаем серверу. */
export function setTheme(theme: Theme): void {
  current = theme;
  paint(theme);
  writeSession(theme);
  notify();

  // Сохранение на сервере — вторично: если сокет сейчас закрыт, тема всё
  // равно уже применена и переживёт перезагрузку вкладки.
  void request('prefs.set', { theme }).catch(() => undefined);
}

/**
 * Подтягивает выбор с сервера после входа. Если в этой вкладке человек уже
 * что-то выбрал, серверное значение не навязываем — иначе переключатель
 * дёргался бы туда-обратно на каждом соединении.
 */
export async function syncThemeFromServer(): Promise<void> {
  if (readSession()) return;
  try {
    const prefs = await request<{ theme: Theme }>('prefs.get');
    if (!isTheme(prefs.theme) || prefs.theme === current) return;
    current = prefs.theme;
    paint(current);
    notify();
  } catch {
    /* не критично: останется то, что пришло из SSR */
  }
}
